import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const { formatPrice, loading: currencyLoading } = useCurrency();
  const navigate = useNavigate();

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <ShoppingBag className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h1 className="font-serif text-4xl mb-4">Your Cart is Empty</h1>
          <p className="text-gray-600 mb-8">
            Add some beautiful gemstones to your collection
          </p>
          <Button
            onClick={() => navigate('/shop')}
            className="bg-black text-white hover:bg-gray-900"
            size="lg"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-serif text-4xl mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-4 p-4 border border-gray-200 rounded-lg"
                >
                  {/* Product Image */}
                  <img
                    src={item.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=200&q=80'}
                    alt={item.product_name}
                    className="w-24 h-24 object-cover rounded"
                  />

                  {/* Product Details */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {item.product_name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {item.carat_weight} ct • {item.gem_type}
                    </p>
                    <p className="font-serif text-xl">
                      {currencyLoading ? '...' : formatPrice(item.price_at_time, item.currency || 'USD')}
                    </p>
                  </div>

                  {/* Price and Remove */}
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => handleRemove(item.product_id)}
                      className="text-red-500 hover:text-red-700 p-2"
                      aria-label="Remove from cart"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>

                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">Unique Item</p>
                      <p className="text-xs text-gray-500">Qty: 1</p>
                    </div>

                    <p className="font-serif text-xl font-semibold mt-2">
                      {currencyLoading ? '...' : formatPrice(item.price_at_time, item.currency || 'USD')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="border border-gray-200 rounded-lg p-6 sticky top-4">
              <h2 className="font-serif text-2xl mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">
                    {currencyLoading ? '...' : formatPrice(cart.subtotal, cart.items[0]?.currency || 'USD')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Items</span>
                  <span className="font-medium">
                    {cart.items.reduce((sum, item) => sum + item.quantity, 0)}
                  </span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-serif">
                    <span>Total</span>
                    <span>{currencyLoading ? '...' : formatPrice(cart.subtotal, cart.items[0]?.currency || 'USD')}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full bg-black text-white hover:bg-gray-900 mb-4"
                size="lg"
              >
                Proceed to Checkout
              </Button>

              <Button
                onClick={() => navigate('/shop')}
                variant="outline"
                className="w-full border-black hover:bg-gray-100"
              >
                Continue Shopping
              </Button>

              <div className="mt-6 p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">
                  <strong>Secure Checkout</strong><br />
                  Your payment information is encrypted and secure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
